<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '../../lib/api/client';
  import type { RouteSegment, TimelineDay, TimelineNote, TimelineStay, TimelineTrip, TripAway } from '../../lib/api/types';
  import { clock } from '../../lib/utils/time';
  import { locale, t } from '../../lib/i18n/index.ts';
  import RouteMap from '../../lib/components/activities/RouteMap.svelte';
  import { categoryIcon, distance, modeIcon, modeLabel, placeName, shortDate, timelineBase } from './format';

  // Trips lists stretches of at least a night away from the home city
  // (005 FR-040). Opening one shows the whole trip as one view (005 FR-040): its
  // travel there and back on a map, and its stays and trips as a timeline;
  // each of its days also opens in the Day tab. On the member's own, the
  // trip lists its days' notes (spec 005 FR-042).
  let { groupId, userId, self, onDay, initialStart }: {
    groupId?: string;
    userId: string;
    self: boolean;
    onDay: (date: string) => void;
    // initialStart opens the trip away that started then, as Records does.
    initialStart?: string;
  } = $props();

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let trips = $state<TripAway[] | null>(null);
  let open = $state(-1);
  let error = $state('');
  // entries and segments are the open trip's stays and trips in time order,
  // and its travel for the map.
  type Entry = { stay?: TimelineStay; trip?: TimelineTrip; start: string };
  let entries = $state<Entry[] | null>(null);
  let segments = $state<RouteSegment[]>([]);
  // notes are the open trip's notes: dayNotes those on its days, and
  // entryNotes those on its stays and trips by their ID.
  let dayNotes = $state<TimelineNote[]>([]);
  let entryNotes = $state<Map<string, string>>(new Map());
  let pending = untrack(() => initialStart);

  async function openTrip(i: number) {
    open = open === i ? -1 : i;
    entries = null;
    segments = [];
    dayNotes = [];
    entryNotes = new Map();
    if (open < 0 || !trips) return;
    const t = trips[i];
    try {
      const days = await Promise.all(t.days.map((d) =>
        api.get<TimelineDay>(`${timelineBase(groupId, userId, self)}/timeline/days/${d}?tz=${encodeURIComponent(tz)}`)));
      if (open !== i) return;
      const from = new Date(t.start).getTime(), to = new Date(t.end).getTime();
      // A stay that only touches the trip, the night before leaving or the
      // one after coming back, is at home, not part of it.
      const inTrip = (s: string, e: string) => new Date(e).getTime() > from && new Date(s).getTime() < to;
      const all: Entry[] = [];
      for (const d of days) {
        for (const s of d.stays) if (inTrip(s.start, s.end)) all.push({ stay: s, start: s.start });
        for (const tr of d.trips) if (inTrip(tr.start, tr.end)) all.push({ trip: tr, start: tr.start });
      }
      all.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      entries = all;
      const notes = days.flatMap((d) => d.notes ?? []);
      dayNotes = notes.filter((n) => !n.stay_id && !n.trip_id);
      entryNotes = new Map(notes.flatMap((n) => (n.stay_id ? [[n.stay_id, n.text]] : n.trip_id ? [[n.trip_id, n.text]] : [])) as [string, string][]);
      segments = all.filter((e) => e.trip && e.trip.route.length > 0)
        .map((e) => ({ gap_before: e.trip!.gap, points: e.trip!.route.map((p) => ({ lat: p.lat, lon: p.lon })) }));
    } catch (e: any) {
      error = e.message;
    }
  }

  $effect(() => {
    load(userId);
  });

  async function load(_user: string) {
    error = '';
    try {
      trips = await api.get<TripAway[]>(`${timelineBase(groupId, userId, self)}/timeline/trips-away?tz=${encodeURIComponent(tz)}`);
      if (pending) {
        const at = Date.parse(pending);
        const i = trips.findIndex((t) => Math.abs(Date.parse(t.start) - at) < 60_000);
        pending = undefined;
        if (i >= 0) openTrip(i);
      }
    } catch (e: any) {
      error = e.message;
      trips = null;
    }
  }

  const places = (trip: TripAway) => trip.cities.map((c) => c.city).join(', ') || t('timelineSomewhereAway');
  const dayLabel = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString(locale(), { weekday: 'short', month: 'short', day: 'numeric' });
</script>

<div class="flex-1 overflow-y-auto p-6">
  <div class="max-w-3xl mx-auto space-y-4">
    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {:else if trips && trips.length === 0}
      <p class="text-sm text-text-muted">{t('timelineNoTripsAway')}</p>
    {:else if trips}
      {#each trips as away, i (away.start)}
        <section class="card">
          <button class="w-full text-left p-5 flex items-center gap-4 hover:opacity-80" onclick={() => openTrip(i)}>
            <i aria-hidden="true" class="las la-suitcase text-2xl"></i>
            <span class="flex-1 min-w-0">
              <span class="block font-semibold truncate">{places(away)}</span>
              <span class="block text-sm text-text-muted">
                {away.ongoing
                  ? t('timelineTripAwayNow', { start: shortDate(away.start), nights: away.nights })
                  : t('timelineTripAwayLine', { start: shortDate(away.start), end: shortDate(away.end), nights: away.nights })}
              </span>
            </span>
            <i aria-hidden="true" class="las {open === i ? 'la-angle-up' : 'la-angle-down'} text-text-muted"></i>
          </button>
          {#if open === i}
            <div class="px-5 pb-5 space-y-3">
              <p class="text-sm text-text-muted">{away.ongoing
                ? t('timelineTripLeft', { city: away.home.city, date: shortDate(away.start), time: clock(away.start) })
                : t('timelineTripLeftBack', { city: away.home.city, date: shortDate(away.start), time: clock(away.start), backDate: shortDate(away.end), backTime: clock(away.end) })}</p>
              {#if segments.length > 0}
                <RouteMap {segments} height="h-64" />
              {/if}
              {#if entries === null}
                <p class="text-sm text-text-muted">{t('timelineTripLoading')}</p>
              {:else}
                <ul>
                  {#each entries as e, k (k)}
                    {@const note = e.stay?.id ? entryNotes.get(e.stay.id) : e.trip?.id ? entryNotes.get(e.trip.id) : undefined}
                    <li class="py-1.5 flex items-center gap-3 text-sm">
                      {#if e.stay}
                        <i aria-hidden="true" class="las {categoryIcon(e.stay.category)} text-lg" style="color: var(--theme-green);"></i>
                        <span class="flex-1 min-w-0 truncate">{e.stay.city ? t('timelinePlaceInCity', { place: placeName(e.stay), city: e.stay.city }) : placeName(e.stay)}</span>
                      {:else if e.trip}
                        <i aria-hidden="true" class="las {modeIcon(e.trip.mode)} text-lg"></i>
                        <span class="flex-1 min-w-0 truncate">{e.trip.gap && e.trip.mode !== 'flight' ? t('timelineNoLocations') : t('timelineModeDistance', { mode: modeLabel(e.trip.mode), distance: distance(e.trip.distance_m) })}</span>
                      {/if}
                      <span class="text-text-muted">{t('timelineDateTime', { date: shortDate(e.start), time: clock(e.start) })}</span>
                    </li>
                    {#if note}
                      <li class="pl-8 pb-1.5 text-sm whitespace-pre-wrap break-words"><i aria-hidden="true" class="las la-sticky-note mr-1 text-text-muted"></i><span class="sr-only">{`${t('timelineNoteSr')} `}</span>{note}</li>
                    {/if}
                  {/each}
                </ul>
              {/if}
              {#if dayNotes.length}
                <div>
                  <h3 class="text-sm font-semibold mb-1">{t('timelineNotes')}</h3>
                  <ul class="space-y-1">
                    {#each dayNotes as n (n.id)}
                      <li class="text-sm whitespace-pre-wrap break-words"><span class="text-text-muted">{t('timelineNoteDay', { day: dayLabel(n.date) })}</span> {n.text}</li>
                    {/each}
                  </ul>
                </div>
              {/if}
              <div class="flex gap-2 flex-wrap">
                {#each away.days as d (d)}
                  <button class="btn btn-secondary btn-sm" onclick={() => onDay(d)}><i aria-hidden="true" class="las la-calendar-day mr-1"></i>{dayLabel(d)}</button>
                {/each}
              </div>
            </div>
          {/if}
        </section>
      {/each}
    {/if}
  </div>
</div>
